import React, { useEffect, useState } from "react";
import { ShoppingBag, X, BarChart3, TrendingUp, Package, Globe, MapPin } from "lucide-react";
import { api, authHeaders, resolveImage } from "../lib/api";

function flagEmoji(code) {
    if (!code || code.length !== 2) return "";
    const up = code.toUpperCase();
    const A = 0x1f1e6;
    return String.fromCodePoint(
        up.charCodeAt(0) - 65 + A,
        up.charCodeAt(1) - 65 + A
    );
}

function Stat({ label, value, accent, testId }) {
    return (
        <div
            data-testid={testId}
            className="bg-[#121212] border border-white/10 rounded-xl p-4"
        >
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">
                {label}
            </div>
            <div
                className={`font-display text-3xl ${accent ? "kw-orange" : "text-white"}`}
            >
                {value}
            </div>
        </div>
    );
}

function Bar({ buys, donts }) {
    const total = buys + donts;
    const buyPct = total ? (buys / total) * 100 : 0;
    const dontPct = total ? (donts / total) * 100 : 0;
    return (
        <div className="flex h-2 rounded-full overflow-hidden bg-white/5">
            <div
                className="kw-orange-bg"
                style={{ width: `${buyPct}%` }}
                title={`Buys: ${buys}`}
            />
            <div
                className="bg-white/25"
                style={{ width: `${dontPct}%` }}
                title={`Don't buys: ${donts}`}
            />
        </div>
    );
}

function Timeline({ data }) {
    const max = Math.max(1, ...data.map((d) => d.buy + d.dont_buy));
    return (
        <div
            data-testid="analytics-timeline"
            className="flex items-end gap-2 h-32 mt-4"
        >
            {data.map((d) => {
                const total = d.buy + d.dont_buy;
                const hTotal = (total / max) * 100;
                const hBuy = total ? (d.buy / total) * hTotal : 0;
                const hDont = total ? (d.dont_buy / total) * hTotal : 0;
                const label = d.date.slice(5); // MM-DD
                return (
                    <div
                        key={d.date}
                        className="flex-1 flex flex-col items-center gap-1"
                        title={`${d.date} · ${d.buy} buys · ${d.dont_buy} skips`}
                    >
                        <div className="flex-1 w-full flex flex-col justify-end rounded-md overflow-hidden bg-white/5">
                            <div
                                className="bg-white/25"
                                style={{ height: `${hDont}%` }}
                            />
                            <div
                                className="kw-orange-bg"
                                style={{ height: `${hBuy}%` }}
                            />
                        </div>
                        <span className="text-[10px] text-white/40 tracking-wider">
                            {label}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

export default function AnalyticsPanel() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = async () => {
        setLoading(true);
        setError("");
        try {
            const { data } = await api.get("/admin/analytics", {
                headers: authHeaders(),
            });
            setData(data);
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to load analytics");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    if (loading && !data) {
        return (
            <div
                data-testid="analytics-loading"
                className="bg-[#121212] border border-white/10 rounded-2xl p-6 text-white/60"
            >
                Loading analytics…
            </div>
        );
    }
    if (error) {
        return (
            <div
                data-testid="analytics-error"
                className="bg-[#121212] border border-white/10 rounded-2xl p-6 text-[#FF5A00]"
            >
                {error}
            </div>
        );
    }
    if (!data) return null;

    const { totals, per_product: perProduct, timeline, top_product, geo } = data;
    const topCountries = geo?.top_countries || [];
    const topRegions = geo?.top_regions || [];
    const geoMax = Math.max(1, ...topCountries.map((c) => c.count));
    const regMax = Math.max(1, ...topRegions.map((c) => c.count));

    return (
        <section
            data-testid="analytics-panel"
            className="bg-[#121212] border border-white/10 rounded-2xl p-5 space-y-5"
        >
            <div className="flex items-center justify-between">
                <h2 className="font-display text-2xl uppercase tracking-tight flex items-center gap-2">
                    <BarChart3 size={20} className="kw-orange" /> Analytics
                </h2>
                <button
                    data-testid="analytics-refresh"
                    onClick={load}
                    className="text-[11px] uppercase tracking-widest text-white/50 hover:text-white"
                >
                    Refresh
                </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <Stat
                    label="Products"
                    value={totals.products}
                    testId="stat-products"
                />
                <Stat
                    label="Total swipes"
                    value={totals.swipes}
                    testId="stat-swipes"
                />
                <Stat
                    label="Buys"
                    value={totals.buys}
                    accent
                    testId="stat-buys"
                />
                <Stat
                    label="Don't buys"
                    value={totals.dont_buys}
                    testId="stat-dontbuys"
                />
                <Stat
                    label="Visits"
                    value={totals.visits ?? 0}
                    testId="stat-visits"
                />
            </div>

            <div className="bg-black/40 border border-white/10 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">
                        Buy rate
                    </div>
                    <div
                        data-testid="stat-buy-rate"
                        className="font-display text-xl kw-orange"
                    >
                        {totals.buy_rate}%
                    </div>
                </div>
                <Bar buys={totals.buys} donts={totals.dont_buys} />
                <div className="flex justify-between text-[10px] uppercase tracking-widest text-white/40 mt-2">
                    <span className="flex items-center gap-1">
                        <ShoppingBag size={10} /> Buys
                    </span>
                    <span className="flex items-center gap-1">
                        <X size={10} /> Skips
                    </span>
                </div>
            </div>

            {top_product && (
                <div
                    data-testid="top-product"
                    className="flex items-center gap-4 bg-black/40 border border-white/10 rounded-xl p-4"
                >
                    <img
                        src={resolveImage(top_product.image_url)}
                        alt={top_product.title}
                        className="w-14 h-14 rounded-lg object-cover"
                    />
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 flex items-center gap-1">
                            <TrendingUp size={10} /> Top performer
                        </div>
                        <div className="font-semibold truncate">
                            {top_product.title}
                        </div>
                        <div className="text-xs text-white/50">
                            {top_product.buys} buys · {top_product.buy_rate}%
                            buy rate
                        </div>
                    </div>
                </div>
            )}

            <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 flex items-center gap-1">
                    <BarChart3 size={10} /> Last 7 days
                </div>
                <Timeline data={timeline} />
            </div>

            <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2 flex items-center gap-1">
                    <Package size={10} /> Per product
                </div>
                {perProduct.length === 0 ? (
                    <div className="text-white/40 text-sm py-4 text-center border border-dashed border-white/10 rounded-xl">
                        No swipes yet — share your shop to start collecting
                        data.
                    </div>
                ) : (
                    <div className="space-y-3" data-testid="per-product-list">
                        {perProduct.map((p) => (
                            <div
                                key={p.id}
                                data-testid={`analytics-product-${p.id}`}
                                className="bg-black/40 border border-white/10 rounded-xl p-3"
                            >
                                <div className="flex items-center gap-3 mb-2">
                                    <img
                                        src={resolveImage(p.image_url)}
                                        alt={p.title}
                                        className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium truncate text-sm">
                                            {p.title}
                                        </div>
                                        <div className="text-[11px] text-white/40">
                                            {p.total} swipes ·{" "}
                                            <span className="kw-orange">
                                                {p.buys} buys
                                            </span>{" "}
                                            · {p.dont_buys} skips
                                        </div>
                                    </div>
                                    <div className="font-display text-lg kw-orange">
                                        {p.buy_rate}%
                                    </div>
                                </div>
                                <Bar buys={p.buys} donts={p.dont_buys} />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div data-testid="analytics-geo">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2 flex items-center gap-1">
                    <Globe size={10} /> Visitors by country
                </div>
                {topCountries.length === 0 ? (
                    <div className="text-white/40 text-sm py-4 text-center border border-dashed border-white/10 rounded-xl">
                        No visits recorded yet.
                    </div>
                ) : (
                    <ul
                        data-testid="analytics-countries"
                        className="space-y-2"
                    >
                        {topCountries.map((c) => {
                            const pct = (c.count / geoMax) * 100;
                            return (
                                <li
                                    key={`${c.country}-${c.country_code}`}
                                    data-testid={`country-${c.country_code || "xx"}`}
                                    className="bg-black/40 border border-white/10 rounded-xl p-3"
                                >
                                    <div className="flex items-center gap-3 text-sm">
                                        <span className="text-xl leading-none">
                                            {flagEmoji(c.country_code) || "🌐"}
                                        </span>
                                        <span className="flex-1 truncate">
                                            {c.country}
                                        </span>
                                        <span className="text-white/60 text-xs">
                                            {c.count}
                                        </span>
                                    </div>
                                    <div className="mt-2 h-1.5 rounded-full overflow-hidden bg-white/5">
                                        <div
                                            className="h-full kw-orange-bg"
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            {topRegions.length > 0 && (
                <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2 flex items-center gap-1">
                        <MapPin size={10} /> Top regions
                    </div>
                    <ul
                        data-testid="analytics-regions"
                        className="space-y-2"
                    >
                        {topRegions.map((r, idx) => {
                            const pct = (r.count / regMax) * 100;
                            return (
                                <li
                                    key={`${r.country}-${r.region}-${idx}`}
                                    data-testid={`region-${idx}`}
                                    className="bg-black/40 border border-white/10 rounded-xl p-3"
                                >
                                    <div className="flex items-center gap-3 text-sm">
                                        <span className="text-lg leading-none">
                                            {flagEmoji(r.country_code) || "📍"}
                                        </span>
                                        <span className="flex-1 truncate">
                                            <span className="text-white/90">
                                                {r.region}
                                            </span>
                                            <span className="text-white/40 text-xs ml-2">
                                                {r.country}
                                            </span>
                                        </span>
                                        <span className="text-white/60 text-xs">
                                            {r.count}
                                        </span>
                                    </div>
                                    <div className="mt-2 h-1.5 rounded-full overflow-hidden bg-white/5">
                                        <div
                                            className="h-full kw-orange-bg"
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </section>
    );
}
